import React from "react";
import { useCallback, useEffect, useRef } from "react";
import Feature from "ol/Feature";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import Icon from "ol/style/Icon";
import { Text } from "ol/style";
import { Coordinate } from "ol/coordinate";
import Style from "ol/style/Style";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Annotation } from ".";
import { useMap } from "../../hooks/incontext/useMap";
import { icon, makeText } from "../../utils/object";
import { useInteractionEvent } from "../../hooks/incontext/useInteractionEvent";
import { Geometry } from "ol/geom";

export interface CustomMarkerProps extends Annotation {
  center: Coordinate;
  selected?: boolean;
  /**
   * 이 마커만 쓸 아이콘 경로. 미지정이면 페이지가 정한 전역 `icon.marker`·`icon.selected` 를 따른다 —
   * 전역값은 화면마다 다르게 덮어써지므로, 어느 화면에서 열어도 같은 모양이어야 하는 마커가 지정한다.
   */
  iconSrc?: string;
}

export const CustomMarker = ({
  center,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
  selected = false,
  iconSrc,
}: CustomMarkerProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<Point>>(
    new Feature(new Point(fromLonLat(center)))
  );
  const annotationLayerRef = useRef<
    VectorLayer<VectorSource<Feature<Geometry>>>
  >(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );
  const annotationStyleRef = useRef(
    new Style({
      text:
        children && !children.props.isPopup
          ? makeText({
              text: children.props.children || "",
              size: children.props.size || 15,
              color: children.props.color ? children.props.color : "black",
              outline: children.props.outline,
              isMarker: true,
            })
          : undefined,
      image: new Icon({
        src: iconSrc ?? icon.marker, // 마커 이미지 경로
        scale: 1,
        anchor: [0.5, 0.5], // 마커 이미지의 앵커 위치
      }),
    })
  );

  const onHoverHandler = useCallback(
    (feature: Feature, pixel?: number[]) => {
      if (feature) {
        if (onHover) {
          onHover({ annotation: annotationRef.current, properties });
        }
      }

      // 수정중일땐 팝업 관여하지 않음
      if (map.getProperties().isModifying) return;

      // Pop up text — 아이콘 영역 내에서만 표시
      if (feature && children?.props.isPopup) {
        const ICON_HIT_RADIUS_PX = 14;
        let isWithinIcon = true;

        if (pixel) {
          const markerCoord = annotationRef.current
            .getGeometry()
            ?.getCoordinates();
          if (markerCoord) {
            const markerPixel = map.getPixelFromCoordinate(markerCoord);
            if (markerPixel) {
              const dx = pixel[0] - markerPixel[0];
              const dy = pixel[1] - markerPixel[1];
              isWithinIcon = Math.sqrt(dx * dx + dy * dy) <= ICON_HIT_RADIUS_PX;
            }
          }
        }

        // 겹친 마커 중 가장 위에 있는 것만 텍스트 표시
        if (isWithinIcon && pixel) {
          const featuresAtPixel = map.getFeaturesAtPixel(pixel).reverse();
          const topmostAnnotation = featuresAtPixel.find(
            (f) => f.getProperties().shape
          );
          if (topmostAnnotation && topmostAnnotation !== annotationRef.current) {
            isWithinIcon = false;
          }
        }

        if (isWithinIcon) {
          annotationStyleRef.current.setText(
            makeText({
              text: children.props.children || "",
              size: children.props.size || 15,
              color: children.props.color ? children.props.color : "black",
              outline: children.props.outline,
              isMarker: true,
            })
          );
        } else {
          annotationStyleRef.current.setText(new Text());
        }
        annotationRef.current.setStyle(annotationStyleRef.current);
      }
    },
    [children, map, onHover, properties]
  );

  const onLeaveHandler = useCallback(() => {
    if (onLeave) {
      onLeave();
    }

    // 수정중일땐 팝업 관여하지 않음
    if (map.getProperties().isModifying) return;

    // 팝업 텍스트 숨김
    if (children?.props.isPopup) {
      annotationStyleRef.current.setText(new Text());
      annotationRef.current.setStyle(annotationStyleRef.current);
    }
  }, [children, map, onLeave]);

  const onClickHandler = () => {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
    // 선택된 Feature에 대한 작업 수행
    // 예: 스타일 변경, 정보 표시 등
  };

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    onLeave: onLeaveHandler,
    isDisabledSelection,
  });

  useEffect(() => {
    if (annotationRef.current) {
      const geometry = annotationRef.current.getGeometry() as Point;
      geometry.setCoordinates(fromLonLat(center));
    }
  }, [center]);

  useEffect(() => {
    if (annotationLayerRef.current) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    // icon.selected 가 초기화되지 않은 경로(예: 비-어노테이션 CustomMarker 사용처)는 marker 폴백
    const selectedSrc = icon.selected || icon.marker;
    const pageIconSrc = selected ? selectedSrc : icon.marker;
    annotationStyleRef.current.setImage(
      new Icon({
        opacity,
        src: iconSrc ?? pageIconSrc, // 마커 이미지 경로
        scale: 0.5,
        anchor: [0.5, 0.5], // 마커 이미지의 앵커 위치
      })
    );

    annotationRef.current.setStyle(annotationStyleRef.current);
  }, [selected, opacity, iconSrc]);

  useEffect(() => {
    if (!children?.props?.color) return;
    const gottonText = annotationStyleRef.current.getText();
    if (gottonText && gottonText.getFill()) {
      gottonText.getFill()?.setColor(children.props.color);
    }

    annotationRef.current.setStyle(annotationStyleRef.current);
  }, [color, children]);

  useEffect(() => {
    if (annotationLayerRef.current && children && !children.props.isPopup) {
      annotationStyleRef.current.setText(
        makeText({
          text: children?.props?.children || "",
          size: children?.props?.size || 15,
          color: children?.props?.color ? children.props.color : "black",
          outline: children?.props?.outline,
          isMarker: true,
        })
      );
    }
  }, [children]);

  useEffect(() => {
    if (!children) {
      annotationStyleRef.current.setText(new Text());
    }
  }, [children]);

  useEffect(() => {
    annotationRef.current.setProperties({
      ...properties,
      shape: "Marker",
      isModifying: false,
      source: annotationLayerRef.current.getSource(),
      layer: annotationLayerRef.current,
      hasPopup: children ? children?.props.isPopup : false,
    });
  }, [properties]);

  useEffect(() => {
    const annotationLayerCurrent = annotationLayerRef.current;
    map.addLayer(annotationLayerCurrent);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      annotationLayerCurrent.getSource()?.clear();
      map.removeLayer(annotationLayerCurrent);
    };
  }, [map]);

  // 반환 DOM이 없는 컴포넌트 (OpenLayers 레이어로 직접 렌더링)
  return null;
};
